/* eslint-disable react-hooks/exhaustive-deps */
import "./Home.css";
import Button from "../common/button/Button";
// import Search from "../common/search/Search";
import { useCallback, useEffect, useState } from "react";
// import { useMenu } from "../common/menuContext/MenuContext";
import { Search, useMenu } from "@voicecare.ai/ui-library";
import { useTheme } from "../../ThemeContext";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { message } from "antd";
import {
  getBatches,
  getCallsList,
  downloadBatchWhitesheet,
  downloadBatchAllCallDetails,
  cancelCallsOrBatch,
  getFacilitiesWithUnassignedStaff,
  getCustomerDetails,
  getStaff,
} from "../../utils/apiClient";
import { useDetectMyRole, useURLFiltering } from "../../custom-hooks";
import FacilityActionPopup from "../common/FacilityActionPopup";
import StickyFlexTable from "../common/StickyFlexTable";
import CallStatus from "./CallStatus";
import TypedConfirmPopup from "../common/TypedConfirmPopup";
import ThemedTooltip from "../common/ThemedTooltip";
import OverflowMenu from "../common/OverflowMenu";
import ThemedChip from "../common/ThemedChip";
import ManagementToolbar from "../common/ManagementToolbar";
import { formatDateTime, isCancelable } from "../../utils/date-utils";
import {
  BATCH_FILTER_GROUPS,
  SEARCH_PLACEHOLDERS,
} from "../../constants/filter-constants";

/**
 * Formats scheduled slot UTC time to locale time
 * @param {string} utcTimeString - UTC time in format "MM-DD-YYYY_HH:MM:SS"
 * @returns {string} Formatted time string
 */
const formatScheduledSlot = (utcTimeString) => {
  try {
    // Parse the UTC time string "08-27-2025_05:30:00"
    const [datePart, timePart] = utcTimeString.split("_");
    const [month, day, year] = datePart.split("-");
    const [hours, minutes, seconds] = timePart.split(":");

    // Create UTC date
    const utcDate = new Date(
      Date.UTC(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds || 0)
      )
    );

    // Get local date
    const localDate = new Date(
      utcDate.toLocaleString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );

    // Get today, yesterday, and tomorrow dates (normalized to midnight for comparison)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Normalize the local date to midnight for comparison
    const localDateNormalized = new Date(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate()
    );

    // Format time (12-hour format with AM/PM)
    const timeString = localDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    // Check if date is yesterday, today, or tomorrow
    if (localDateNormalized.getTime() === yesterday.getTime()) {
      return `Yesterday, ${timeString}`;
    } else if (localDateNormalized.getTime() === today.getTime()) {
      return `Today, ${timeString}`;
    } else if (localDateNormalized.getTime() === tomorrow.getTime()) {
      return `Tomorrow, ${timeString}`;
    } else {
      // For other dates, show full date and time
      const dateString = localDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `${dateString}, ${timeString}`;
    }
  } catch (error) {
    console.error("Error formatting scheduled slot:", error);
    return utcTimeString; // Return original if parsing fails
  }
};

// Opens client-ui /preview-login: handoff → refresh-token → dashboard
const getDefaultPreviewAgentUrl = () => {
  const gatewayUrl = (process.env.REACT_APP_API_GATEWAY_URL || "").toLowerCase();

  if (gatewayUrl.includes("devkong.voicecare.ai")) {
    return "https://dev-client-ui.voicecare.ai/preview-login";
  }

  if (gatewayUrl.includes("qakong.voicecare.ai")) {
    return "https://qa-client-ui.voicecare.ai/preview-login";
  }

  return "https://preview-agent.voicecare.ai/preview-login";
};

const DEFAULT_PREVIEW_AGENT_URL = getDefaultPreviewAgentUrl();

const Home = () => {
  const { themeMode, currentTheme } = useTheme();
  const { selectMenu } = useMenu();
  const { role } = useDetectMyRole();
  const [batchData, setBatchData] = useState([]);
  const [loading, setLoading] = useState(false);

  // URL-based filtering
  const { selectedFilters, updateFilters, getAPIParams } =
    useURLFiltering("batch");

  // Filter options state
  const [branches, setBranches] = useState([]);
  const [usecases, setUsecases] = useState([]);
  const [subUsecases, setSubUsecases] = useState([]);
  const [uploaders, setUploaders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page2Visible, setPage2Visible] = useState(false);
  const navigate = useNavigate();
  const [originalData, setOriginalData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [callStatusRows, setCallStatusRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [cancelPopupVisible, setCancelPopupVisible] = useState(false);
  const [batchToCancel, setBatchToCancel] = useState(null);
  // actionsRow state removed; OverflowMenu handles its own open/close state
  const [now, setNow] = useState(Date.now());
  const [copiedItem, setCopiedItem] = useState(null); // Stores "id-<batchId>" or "alias-<batchId>"

  // 🆕 New UI Promotional Banner
  const [showBanner, setShowBanner] = useState(
    () => sessionStorage.getItem("newUIBannerDismissed") !== "true"
  );
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const handleDismissBanner = () => {
    sessionStorage.setItem("newUIBannerDismissed", "true");
    setShowBanner(false);
  };

  const openPreviewAgentHandoff = useCallback(() => {
    const previewUrl =
      process.env.REACT_APP_PREVIEW_AGENT_URL || DEFAULT_PREVIEW_AGENT_URL;
    let previewOrigin;
    try {
      previewOrigin = new URL(previewUrl).origin;
    } catch {
      previewOrigin = "https://preview-agent.voicecare.ai";
    }

    let handshakeTimer;
    const onMessage = (event) => {
      if (event.origin !== previewOrigin) return;
      if (event.data?.type !== "PREVIEW_AGENT_READY") return;

      const access_token = sessionStorage.getItem("access_token");
      const api_key = sessionStorage.getItem("api_key");
      if (access_token && api_key && event.source) {
        event.source.postMessage(
          {
            type: "VOICECARE_CUSTOMER_UI_AUTH",
            access_token,
            api_key,
          },
          previewOrigin
        );
      }
      window.clearTimeout(handshakeTimer);
      window.removeEventListener("message", onMessage);
    };

    window.addEventListener("message", onMessage);
    window.open(previewUrl, "_blank");
    handshakeTimer = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
    }, 60000);
  }, []);

  // 🏢 Facility Action Notification State
  const [facilityActionPopupVisible, setFacilityActionPopupVisible] =
    useState(false);
  const [facilityActionLoading, setFacilityActionLoading] = useState(false);
  const [facilitiesRequiringActionCount, setFacilitiesRequiringActionCount] =
    useState(0);

  // Update time-dependent UI every 30s
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const CANCEL_DISABLE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes safety window

  // formatDateTime now imported from utils

  // Fetch filter options on component mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch branches and usecases from customer details
        const customerDetails = await getCustomerDetails();
        if (
          customerDetails.statusCode === "SUCCESS" &&
          customerDetails.response?.useCasesRef
        ) {
          const useCasesRef = customerDetails.response.useCasesRef;

          // Extract unique branches
          const uniqueBranches = [
            ...new Set(useCasesRef.map((uc) => uc.branch)),
          ];
          setBranches(
            uniqueBranches.map((branch) => ({
              label: branch.charAt(0).toUpperCase() + branch.slice(1),
              value: branch,
            }))
          );

          // Extract unique usecases
          const uniqueUsecases = [
            ...new Set(useCasesRef.map((uc) => uc.usecase)),
          ];
          setUsecases(
            uniqueUsecases.map((usecase) => ({
              label: usecase
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              value: usecase,
            }))
          );

          // Extract unique subusecases
          const uniqueSubUsecases = [
            ...new Set(useCasesRef.map((uc) => uc.subUsecase)),
          ];
          setSubUsecases(
            uniqueSubUsecases.map((subUsecase) => ({
              label: subUsecase
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              value: subUsecase,
            }))
          );
        }

        // Fetch staff members for uploader filter
        const staffResponse = await getStaff({
          include_inactive: true,
          includeMe: true,
        });
        if (staffResponse.statusCode === "SUCCESS" && staffResponse.response) {
          const uniqueUploaders = [
            ...new Set(staffResponse.response.map((staff) => staff.name)),
          ].filter(Boolean);
          setUploaders(
            uniqueUploaders.map((name) => ({
              label: name,
              value: name,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilterOptions();
  }, []);

  useEffect(() => {
    const apiParams = getAPIParams(selectedFilters);
    batchDataSetter(page * rowsPerPage, rowsPerPage, apiParams);
  }, [page, rowsPerPage, selectedFilters, getAPIParams]);

  // 🏢 Facility Action Check - Check for facilities requiring action on Home page load
  useEffect(() => {
    const checkFacilityAction = async () => {
      // Check if user is admin or supervisor
      const isAdminOrSupervisor =
        role && (role === "admin" || role === "supervisor");

      if (!isAdminOrSupervisor) {
        console.log("🏢 Facility check skipped - user is not admin/supervisor");
        return;
      }

      // Check if facilityCheck session variable exists and is false
      const facilityCheck = sessionStorage.getItem("facilityCheck");

      if (facilityCheck !== "false") {
        console.log(
          "🏢 Facility check skipped - session variable not set or already processed"
        );
        return;
      }

      console.log(
        "🏢 Checking facilities requiring action for admin/supervisor user..."
      );

      try {
        setFacilityActionLoading(true);
        const response = await getFacilitiesWithUnassignedStaff();

        if (
          response.statusCode === "SUCCESS" &&
          response.response &&
          typeof response.response === "object"
        ) {
          const {
            facilities_with_unassigned_staff = [],
            facilities_with_unassigned_rendering_providers = [],
          } = response.response;

          // Combine both arrays and remove duplicates
          const combinedFacilityIds = [
            ...facilities_with_unassigned_staff,
            ...facilities_with_unassigned_rendering_providers,
          ];

          // Remove duplicates using Set
          const uniqueFacilityIds = [...new Set(combinedFacilityIds)];

          if (uniqueFacilityIds.length > 0) {
            console.log(
              "🚨 Found facilities requiring action:",
              uniqueFacilityIds.length
            );
            console.log(
              "📊 Staff issues:",
              facilities_with_unassigned_staff.length,
              "Provider issues:",
              facilities_with_unassigned_rendering_providers.length
            );
            setFacilitiesRequiringActionCount(uniqueFacilityIds.length);
            setFacilityActionPopupVisible(true);

            // Remove the session variable immediately to prevent showing popup again
            sessionStorage.removeItem("facilityCheck");
          } else {
            console.log("✅ No facilities requiring action found");
            // Remove session variable even if no action needed
            sessionStorage.removeItem("facilityCheck");
          }
        } else {
          console.log(
            "✅ No facilities requiring action found (unexpected response format)"
          );
          // Remove session variable even if no action needed
          sessionStorage.removeItem("facilityCheck");
        }
      } catch (error) {
        console.error("❌ Error checking facilities requiring action:", error);
        // Remove session variable on error to prevent infinite retries
        sessionStorage.removeItem("facilityCheck");
      } finally {
        setFacilityActionLoading(false);
      }
    };

    // Small delay to ensure component is fully mounted
    const timeoutId = setTimeout(() => {
      checkFacilityAction();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [role]); // Only depend on role

  const batchDataSetter = async (
    skip = page * rowsPerPage,
    limit = rowsPerPage,
    apiParams = {}
  ) => {
    setLoading(true);
    try {
      const response = await getBatches({ skip, limit, ...apiParams });
      console.log("getBatches response:", response); // Debug log
      // Handle both possible response structures
      let batches = [];
      let total = 0;
      if (
        response &&
        Array.isArray(response.batches) &&
        typeof response.totalCount === "number"
      ) {
        batches = response.batches;
        total = response.totalCount;
      } else if (
        response &&
        response.response &&
        Array.isArray(response.response.batches) &&
        typeof response.response.totalCount === "number"
      ) {
        batches = response.response.batches;
        total = response.response.totalCount;
      }
      setTotalCount(total);
      const dataWithSequentialIds = batches.map((item, index) => ({
        ...item,
        sequentialId: (skip + index + 1).toString().padStart(5, "0"),
      }));
      const flatten = flatRowCreator(dataWithSequentialIds);
      setBatchData(flatten);
    } catch (error) {
      setBatchData([]);
      console.error("getBatches error:", error);
    }
    setLoading(false);
  };

  const handleReset = () => {
    setSearchText("");
  };

  /**
   * Handles filter changes with API integration
   *
   * @param {Object} newFilters - New filter values
   */
  const handleFiltersChange = (newFilters) => {
    try {
      updateFilters(newFilters);
      setPage(0); // Reset pagination when filters change
    } catch (error) {
      console.error("Error applying filters:", error);
      message.error("Failed to apply filters. Please try again.");
    }
  };

  /**
   * Creates dynamic filter groups with populated options
   */
  const createFilterGroups = () => {
    return BATCH_FILTER_GROUPS.map((group) => {
      if (group.key === "branch") {
        return {
          ...group,
          options: [{ label: "All Branches", value: "all" }, ...branches],
        };
      }
      if (group.key === "usecase") {
        return {
          ...group,
          options: [{ label: "All Usecases", value: "all" }, ...usecases],
        };
      }
      if (group.key === "subUsecase") {
        return {
          ...group,
          options: [
            { label: "All Sub Usecases", value: "all" },
            ...subUsecases,
          ],
        };
      }
      if (group.key === "uploaderName") {
        return {
          ...group,
          options: [{ label: "All Uploaders", value: "all" }, ...uploaders],
        };
      }
      return group;
    });
  };

  /**
   * Handles debounced search for batch alias/name
   */
  const handleSearchChange = (value) => {
    setSearchQuery(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for 400ms debounce
    const timeout = setTimeout(() => {
      handleFiltersChange({
        ...selectedFilters,
        search: value,
      });
    }, 1000);

    setSearchTimeout(timeout);
  };

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // legacy columns removed

  // statusStyle not used in the current view

  const handleSearchPage2 = (value) => {
    if (page2Visible) {
      setSearchText(value);
      const filteredData = originalData.filter((row) => {
        const searchText = value.toLowerCase();

        // Create a combined patient name for searching
        const patientName = [
          row.memberFirstName,
          row.memberMiddleName,
          row.memberLastName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        // Check if search term matches patient name
        if (patientName.includes(searchText)) {
          return true;
        }

        // Check if search term matches primary member ID
        if (
          row.primaryMemberId &&
          row.primaryMemberId.toLowerCase().includes(searchText)
        ) {
          return true;
        }

        // Check other string fields (call status, outcome, etc.)
        return Object.keys(row).some((key) => {
          // Skip the individual name fields since we already checked the combined name
          if (
            [
              "memberFirstName",
              "memberMiddleName",
              "memberLastName",
              "primaryMemberId",
            ].includes(key)
          ) {
            return false;
          }
          if (typeof row[key] === "string") {
            return row[key].toLowerCase().includes(searchText);
          }
          return false;
        });
      });
      setCallStatusRows(filteredData);
    }
  };

  const handleCallHistoryClick = (id) => {
    selectMenu("Call History");
    sessionStorage.setItem("batchId", id);
    navigate(`/call-history?batch_id=${id}`);
  };

  const handleCallProcessClick = async (id) => {
    if (!id) return; // Do nothing if batchId is missing/empty
    setPage2Visible(true);
    selectMenu("Call Status");
    sessionStorage.setItem("batchId", id); // Store batch ID for refresh functionality
    setLoading(true);
    try {
      const res = await getCallsList({ batchId: id });
      if (res && res.statusCode === "SUCCESS") {
        const flatten = res.response?.calls?.map((row, idx) => ({
          ...row, // Include all original fields including cancelBefore
          id: row.callRequestId || idx,
          patientName: [
            row.memberFirstName,
            row.memberMiddleName,
            row.memberLastName,
          ]
            .filter(Boolean)
            .join(" "),
        }));
        setCallStatusRows(flatten);
        setOriginalData(flatten);
      } else {
        setCallStatusRows([]);
        setOriginalData([]);
      }
    } catch (error) {
      setCallStatusRows([]);
      setOriginalData([]);
      console.error("Error fetching call status data: ", error);
    }
    setLoading(false);
  };

  // unused utilities removed

  // const formatDate = (inputDate) => {
  //   console.log(inputDate, "formatDateDebugger");
  //   const dateParts = inputDate.split("-");
  //   const year = dateParts[2];
  //   const month = dateParts[0];
  //   const day = dateParts[1];
  //   const date = new Date(`${year}-${month}-${day}`);
  //   const monthName = date.toLocaleString("en-us", { month: "short" });
  //   return `${day?.padStart(2, "0")} ${monthName}, ${year}`;
  // };

  // downloadFile helper not used

  // const handleDownload = async (row) => {
  //   // Check if the row has a property for the CSV URI
  //   if (row.customer_input_data_filepath) {
  //     try {
  //       const csvUrlResponse = await getBatchDataCSV(
  //         row.customer_input_data_filepath,
  //         sessionStorage.getItem("mail"),
  //         sessionStorage.getItem("sessionId")
  //       );

  //       const fileUrl = csvUrlResponse.response; // Access the signed URL from the response object
  //       const fileName = 'batch_data.csv'; // Replace with the actual file name if needed

  //       downloadFile(fileUrl, fileName);
  //     } catch (error) {
  //       console.error("Error getting signed URL:", error);
  //     }
  //   } else {
  //     // Optionally, handle the case where the CSV URI is missing or invalid
  //     console.error("CSV URI is missing or invalid");
  //   }
  // };

  // Function to format the date and time
  // function formatDateTime(dateStr, timeStr) {
  //   // Combine date and time strings into a single string
  //   const combinedStr = `${dateStr} ${timeStr}`;

  //   // Parse the combined string into a Date object
  //   const date = new Date(combinedStr);

  //   // Padding function
  //   const pad = (num) => num.toString().padStart(2, "0");

  //   // Extract components from the Date object
  //   const month = pad(date.getMonth() + 1); // Months are zero-based in JS
  //   const day = pad(date.getDate());
  //   const year = date.getFullYear();
  //   const hours = pad(date.getHours());
  //   const minutes = pad(date.getMinutes());
  //   const seconds = pad(date.getSeconds());
  //   const milliseconds = date.getMilliseconds().toString().padStart(3, "0");

  //   return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${milliseconds}`;
  // }

  // padNumber not required

  // const handleDownload = async (row) => {
  //   try {
  //     // Check if the row has a property for the CSV data
  //     if (row.customer_input_data_filepath) {
  //       // Call the API to get the CSV data directly
  //       const csvDataResponse = await getBatchDataCSV(
  //         row.customer_input_data_filepath,
  //         sessionStorage.getItem("mail"),
  //         sessionStorage.getItem("sessionId")
  //       );

  //       // Extract CSV data from the response
  //       const csvData = csvDataResponse; // Assuming csvDataResponse contains CSV data directly

  //       // Create a Blob from the CSV data
  //       const blob = new Blob([csvData], { type: "text/csv" });
  //       const fileName = `${sessionStorage.getItem("providerName")}_${
  //         row.sequentialId
  //       }_${formatDateTime(row.uploaded_date, row.uploaded_time)}.csv`;

  //       console.log({fileName, uploadedDate: row.uploaded_date, uploadedTime: row.uploaded_time}, 'downloadDebugger');
  //       // Create an anchor element to trigger the download
  //       const anchor = document.createElement("a");
  //       anchor.href = URL.createObjectURL(blob);
  //       anchor.setAttribute("download", fileName);
  //       anchor.click();
  //       anchor.remove();
  //     } else {
  //       console.error("CSV data filepath is missing or invalid");
  //       // Inform the user that download is unavailable
  //     }
  //   } catch (error) {
  //     console.error("Error downloading file:", error);
  //     // Display an error message to the user
  //   }
  // };

  // legacy handleDownload removed

  // const handleDownload = (row) => {
  //   if (row.customer_input_data_filepath) {
  //     window.open(row.customer_input_data_filepath, "_blank");
  //   } else {
  //     console.error("Download URL is missing or invalid");
  //   }
  // };

  const handleDownloadBatchWhitesheet = async (row) => {
    const batchId = row.id;
    const batchStatus = row.status?.toLowerCase();

    // Check if batch is completed
    // if (batchStatus !== "completed") {
    //   message.warning(
    //     "AI Call Summary is only available for completed batches. This batch is currently " +
    //       (batchStatus || "pending") +
    //       "."
    //   );
    //   return;
    // }

    try {
      // Show download started message
      message.loading("Download started...", 0); // 0 means it won't auto-hide

      const { blob, filename } = await downloadBatchWhitesheet(
        batchId,
        "excel"
      );

      // Hide loading message
      message.destroy();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename; // Use backend-provided filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Show success message
      message.success("AI Call Summary downloaded successfully!");
    } catch (error) {
      // Hide loading message
      message.destroy();

      console.error("Error downloading batch AI call summary:", error);

      // Handle different error scenarios
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (
          errorData.statusCode === "NOT_FOUND" ||
          error.response.status === 404
        ) {
          // Handle specific backend error for batch not ready or no whitesheets available
          if (
            errorData.detail &&
            errorData.detail.includes("No whitesheets available")
          ) {
            message.error(
              "No AI Call Summary available for this batch. Either the batch is not completed or calls failed during processing."
            );
          // } else if (
          //   errorData.message &&
          //   errorData.message.includes("scheduled")
          // ) {
          //   message.error(
          //     "AI Call Summary is only available for completed batches. This batch is still scheduled."
          //   );
          } else {
            message.error(
              errorData.message ||
              errorData.detail ||
              "AI Call Summary not found for this batch."
            );
          }
        } else {
          // Handle other backend errors
          const userMessage =
            errorData.message ||
            errorData.detail ||
            errorData.error ||
            "Failed to download AI Call Summary. Please try again.";
          message.error(userMessage.replace(/whitesheet/gi, "AI Call Summary"));
        }
      } else if (error.request) {
        // Network error
        message.error(
          "Network error. Please check your connection and try again."
        );
      } else {
        // General error
        message.error("Failed to download AI Call Summary. Please try again.");
      }
    }
  };

  const handleDownloadAllCallDetails = async (row) => {
    const batchId = row.id;
    const batchStatus = row.status?.toLowerCase();

    // Check if batch is completed
    if (batchStatus !== "completed") {
      message.warning(
        "All Call AI Details is only available for completed batches. This batch is currently " +
          (batchStatus || "pending") +
          "."
      );
      return;
    }

    try {
      // Show download started message
      message.loading("Preparing All Call AI Details download...", 0); // 0 means it won't auto-hide

      const { blob, filename } = await downloadBatchAllCallDetails(
        batchId,
        "zip"
      );

      // Hide loading message
      message.destroy();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename; // Use backend-provided filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Show success message
      message.success("All Call AI Details downloaded successfully!");
    } catch (error) {
      // Hide loading message
      message.destroy();

      console.error("Error downloading all call AI details:", error);

      // Handle different error scenarios
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (
          errorData.statusCode === "NOT_FOUND" ||
          error.response.status === 404
        ) {
          // Handle specific backend error for batch not ready or no whitesheets available
          if (
            errorData.detail &&
            errorData.detail.includes("No whitesheets available")
          ) {
            message.error(
              "No All Call AI Details available for this batch. Either the batch is not completed or calls failed during processing."
            );
          } else if (
            errorData.message &&
            errorData.message.includes("scheduled")
          ) {
            message.error(
              "All Call AI Details is only available for completed batches. This batch is still scheduled."
            );
          } else {
            message.error(
              errorData.message ||
              errorData.detail ||
              "All Call AI Details not found for this batch."
            );
          }
        } else {
          // Handle other backend errors
          const userMessage =
            errorData.message ||
            errorData.detail ||
            errorData.error ||
            "Failed to download All Call AI Details. Please try again.";
          message.error(userMessage);
        }
      } else if (error.request) {
        // Network error
        message.error(
          "Network error. Please check your connection and try again."
        );
      } else {
        // General error
        message.error(
          "Failed to download All Call AI Details. Please try again."
        );
      }
    }
  };

  const getStatusVariant = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "cancelled":
        return "error";
      case "scheduled":
        return "warning";
      case "completed":
        return "success";
      default:
        return "default";
    }
  };

  /**
   * Renders email with truncation and tooltip
   * @param {string} email - Email address to render
   * @returns {JSX.Element} Rendered email with optional truncation
   */
  const renderEmail = (email) => {
    if (!email) return "-";

    const maxLength = 24;
    const isTruncated = email.length > maxLength;
    const displayEmail = isTruncated
      ? `${email.substring(0, maxLength)}...`
      : email;

    if (isTruncated) {
      return (
        <ThemedTooltip title={email} placement="top">
          <span style={{ cursor: "pointer" }}>
            {displayEmail}
          </span>
        </ThemedTooltip>
      );
    }

    return email;
  };

  /**
   * Handles copying batch ID/name to clipboard
   * @param {string} text - Text to copy
   * @param {string} type - Type of text being copied ('id' or 'alias')
   * @param {string} batchId - Batch ID for tracking copied state
   * @param {Event} e - Click event
   */
  const handleCopyText = async (text, type, batchId, e) => {
    e.stopPropagation(); // Prevent row click navigation

    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(`${type}-${batchId}`);
      message.success(`Copied: ${text}`);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedItem(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      message.error('Failed to copy to clipboard');
    }
  };

  /**
   * Renders a copy button with tooltip for ID/Name fields
   * @param {string} text - Text to copy
   * @param {string} type - Type ('id' or 'alias')
   * @param {string} batchId - Batch ID for tracking
   */
  const CopyButton = ({ text, type, batchId }) => {
    const isCopied = copiedItem === `${type}-${batchId}`;
    const iconColor = themeMode === 'dark' ? '#9ca3af' : '#6b7280';
    const successColor = '#10b981';

    return (
      <ThemedTooltip
        title={isCopied ? "Copied!" : `Copy ${type === 'alias' ? 'name' : 'ID'}`}
        placement="top"
      >
        <Box
          component="span"
          className="copy-icon"
          onClick={(e) => handleCopyText(text, type, batchId, e)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '6px',
            padding: '3px',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: isCopied ? 1 : 0,
            transition: 'opacity 0.2s, background-color 0.2s',
            '&:hover': {
              opacity: 1,
              backgroundColor: currentTheme.background?.hover || 'rgba(0,0,0,0.08)',
            }
          }}
        >
          {isCopied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={successColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          )}
        </Box>
      </ThemedTooltip>
    );
  };

  const columns = [
    {
      headerName: "Batch ID / Name",
      renderName: "id",
      width: 200,
      renderCell: (row) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {row.alias && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "2px",
                '&:hover .copy-icon': {
                  opacity: 0.7,
                },
                '&:hover .copy-icon:hover': {
                  opacity: 1,
                },
              }}
            >
              <Box
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: currentTheme.text.primary,
                }}
              >
                {row.alias}
              </Box>
              <CopyButton text={row.alias} type="alias" batchId={row.id} />
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              '&:hover .copy-icon': {
                opacity: 0.7,
              },
              '&:hover .copy-icon:hover': {
                opacity: 1,
              },
            }}
          >
            <Box
              sx={{
                color: row.alias
                  ? currentTheme.text.secondary
                  : currentTheme.text.primary,
                fontWeight: row.alias ? 400 : 500,
              }}
            >
              {row.id}
            </Box>
            <CopyButton text={row.id} type="id" batchId={row.id} />
          </Box>
        </Box>
      ),
    },
    {
      headerName: "Status",
      renderName: "status",
      width: 120,
      renderCell: (row) => (
        <ThemedChip variant={getStatusVariant(row.status)}>
          {row.status}
        </ThemedChip>
      ),
    },
    { headerName: "Branch", renderName: "branch", width: 60 },
    {
      headerName: "Usecase",
      renderName: "usecase",
      renderCell: (row) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "0.25rem",
          }}
        >
          <Box
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: currentTheme.text.primary,
            }}
          >
            {row.usecase}
          </Box>
          {row.subUsecase && (
            <ThemedChip variant="default" size="small">
              {row.subUsecase}
            </ThemedChip>
          )}
        </Box>
      ),
    },
    { headerName: "Total Calls", renderName: "totalCalls", width: 70 },
    {
      headerName: "Upload Time",
      renderName: "uploadTimestamp",
      minWidth: 150,
      renderCell: (row) => {
        if (!row.uploadTimestamp) {
          return (
            <Box sx={{ color: currentTheme.text.secondary, fontSize: "0.875rem" }}>
              -
            </Box>
          );
        }

        // Split by comma to separate date and time
        const parts = row.uploadTimestamp.split(', ');
        const date = parts[0] || '';
        const time = parts[1] || '';

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: currentTheme.text.primary,
                marginBottom: "2px",
              }}
            >
              {date}
            </Box>
            {time && (
              <Box
                sx={{
                  fontSize: "0.75rem",
                  color: currentTheme.text.secondary,
                  fontWeight: 400,
                }}
              >
                {time}
              </Box>
            )}
          </Box>
        );
      },
    },
    // { headerName: "Cancel Before", renderName: "cancelBeforeLocal" },
    {
      headerName: "Uploader",
      renderName: "uploaderName",
      minWidth: 120,
      renderCell: (row) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: currentTheme.text.primary,
              marginBottom: "2px",
            }}
          >
            {row.uploaderName || "-"}
          </Box>
          {row.uploaderEmail && (
            <Box
              sx={{
                fontSize: "0.75rem",
                color: currentTheme.text.secondary,
                fontWeight: 400,
              }}
            >
              {renderEmail(row.uploaderEmail)}
            </Box>
          )}
        </Box>
      ),
    },
    {
      headerName: "Scheduled Slots",
      renderName: "scheduledSlots",
      width: 180,
      renderCell: (row) => {
        if (!row.scheduledSlots || row.scheduledSlots.length === 0) {
          return (
            <Box
              sx={{ color: currentTheme.text.secondary, fontSize: "0.875rem" }}
            >
              -
            </Box>
          );
        }

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {row.scheduledSlots.map((slot, index) => (
              <ThemedChip key={index} variant="default" size="small">
                {slot}
              </ThemedChip>
            ))}
          </Box>
        );
      },
    },
    {
      renderName: "actions",
      headerName: "Actions",
      minWidth: 120,
      align: "center",
      fixed: true,
      renderCell: (row) => (
        <Box
          sx={{ display: "flex", gap: 1, justifyContent: "center" }}
          onClick={(e) => e.stopPropagation()}
        >
          <OverflowMenu
            items={getMenuItems(row)}
            ariaLabel="Actions"
            dotSize={22}
          />
        </Box>
      ),
    },
  ];

  const flatRowCreator = (rows) => {
    return rows.map((row, index) => {
      // Process scheduled slots UTC to locale time
      let scheduledSlots = [];
      if (
        row.scheduledSlotsUtc &&
        Array.isArray(row.scheduledSlotsUtc) &&
        row.scheduledSlotsUtc.length > 0
      ) {
        scheduledSlots = row.scheduledSlotsUtc.map((slot) =>
          formatScheduledSlot(slot)
        );
      }

      return {
        ...row,
        id: row.id || index,
        uploadTimestamp: formatDateTime(row.uploadTimestamp),
        cancelBeforeLocal: row.cancelBefore
          ? formatDateTime(row.cancelBefore)
          : "-",
        scheduledSlots: scheduledSlots, // Add formatted scheduled slots
      };
    });
  };

  // legacy callAndProcess columns removed

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleButtonClick = (action) => {
    if (action === "createBatch") {
      navigate("/call-automation");
      selectMenu("Call Automation");
    }
  };

  const handleBack = async () => {
    setPage2Visible(false);
    const apiParams = getAPIParams(selectedFilters);
    await batchDataSetter(page * rowsPerPage, rowsPerPage, apiParams);
    localStorage.removeItem("visibleColumns");
    selectMenu("Home");
  };

  const isBatchCancelable = (row) => {
    return isCancelable(row, now, CANCEL_DISABLE_THRESHOLD_MS);
  };

  // tooltip removed in favor of actions menu

  const openCancelPopup = (row) => {
    setBatchToCancel(row);
    setCancelPopupVisible(true);
  };

  const closeCancelPopup = () => {
    setCancelPopupVisible(false);
    setBatchToCancel(null);
  };

  const confirmCancelBatch = async () => {
    if (!batchToCancel?.id) return;
    try {
      message.loading("Cancelling batch...", 0);
      const res = await cancelCallsOrBatch({
        cancelType: "batch",
        ids: [batchToCancel.id],
      });
      message.destroy();
      if (res?.statusCode === "SUCCESS") {
        message.success("Batch cancelled successfully");
      } else {
        message.success("Cancellation request submitted");
      }
      closeCancelPopup();
      // refresh current page with current filters
      const apiParams = getAPIParams(selectedFilters);
      await batchDataSetter(page * rowsPerPage, rowsPerPage, apiParams);
    } catch (error) {
      message.destroy();
      const userMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to cancel batch";
      message.error(userMsg);
    }
  };

  // 🏢 Handle facility action popup continue button
  const handleFacilityActionContinue = () => {
    setFacilityActionPopupVisible(false);
    selectMenu("Facility");
    navigate("/facility");
  };

  const iconsize = 20;

  const getMenuItems = (row) => {
    return [
      // {
      //   icon: (
      //     <img
      //       src={
      //         themeMode === "dark"
      //           ? "/icons/dark-theme/call-history-dark.png"
      //           : "/icons/light-theme/call-history-light.png"
      //       }
      //       alt="Call History"
      //       style={{ width: iconsize, height: iconsize }}
      //     />
      //   ),
      //   label: "Call History",
      //   onClick: () => handleCallHistoryClick(row?.id),
      // },
      // {
      //   icon: (
      //     <img
      //       src={
      //         themeMode === "dark"
      //           ? "/icons/dark-theme/call-status-dark.png"
      //           : "/icons/light-theme/call-status-light.png"
      //       }
      //       alt="Call Status"
      //       style={{ width: iconsize, height: iconsize }}
      //     />
      //   ),
      //   label: "Call Status",
      //   onClick: () => handleCallProcessClick(row?.id),
      // },
      {
        icon: (
          <img
            src={`/icons/excel-${themeMode === "dark" ? "dark" : "light"
              }-icon.png`}
            alt="Download AI call summary"
            style={{ width: iconsize, height: iconsize }}
          />
        ),
        label: "Download AI call summary",
        onClick: () => handleDownloadBatchWhitesheet(row),
      },
      {
        icon: (
          <img
            src={`/icons/zip-icon-${themeMode === "dark" ? "dark" : "light"
              }.png`}
            alt="Export AI Call Summaries (ZIP)"
            style={{ width: iconsize, height: iconsize }}
          />
        ),
        label: "Export AI Call Summaries (ZIP)",
        onClick: () => handleDownloadAllCallDetails(row),
      },
      {
        icon: (
          <ThemedTooltip
            title="Cancel this entire batch and all its scheduled calls. This action cannot be undone."
            placement="top"
          >
            <img
              src={`/icons/cancel-icon.png`}
              alt="Cancel Batch"
              style={{ width: iconsize, height: iconsize }}
            />
          </ThemedTooltip>
        ),
        label: "Cancel Batch",
        danger: true,
        disabled: !isBatchCancelable(row),
        onClick: () => openCancelPopup(row),
      },
    ];
  };

  // Call status columns moved to the new CallStatus component

  return (
    <div
      className="home-root"
      style={{
        background: currentTheme.homePage.mainBackground,
        color: currentTheme.homePage.textColor,
      }}
    >
      {/* 🆕 New UI Promotional Banner */}
      {showBanner && (
        <div className="new-ui-banner">
          <div className="new-ui-banner__content">
            <p className="new-ui-banner__title">
              Coming soon: New improved Voicecare.AI interface
            </p>
            <p className="new-ui-banner__subtitle">
            A Cleaner Interface, Full Operational Visibility, Agentic AI and Real-Time Awareness
            </p>
          </div>
          <button
            className="new-ui-banner__cta"
            onClick={() => setVideoModalOpen(true)}
          >
            Explore now →
          </button>
          <button
            className="new-ui-banner__close"
            onClick={handleDismissBanner}
            aria-label="Close banner"
          >
            ×
          </button>
        </div>
      )}

      {!page2Visible ? (
        <>
          {loading ? (
            <img
              className="loader"
              src={
                themeMode === "dark"
                  ? "/icons/loader_white.gif"
                  : "/icons/loader_black.gif"
              }
              alt="Loading"
            />
          ) : (
            <StickyFlexTable
              columns={columns}
              rows={batchData || []}
              loading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              containerHeight={showBanner ? "62vh" : "70vh"}
              rowsCount={totalCount}
              rowSelector={(row) => handleCallHistoryClick(row?.id)}
              toolbar={
                <ManagementToolbar
                  module="batch"
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  searchPlaceholder={SEARCH_PLACEHOLDERS.batch}
                  filterGroups={createFilterGroups()}
                  selectedFilters={selectedFilters}
                  onFiltersChange={handleFiltersChange}
                  onAdd={() => handleButtonClick("createBatch")}
                  addButtonLabel="Create Call"
                  showAddButton={true}
                  showFilters={true}
                  showSearch={true}
                  searchBarWidth="98%"
                />
              }
              emptyMessage="No batches found matching your filters. Try adjusting your search criteria."
            />
          )}
        </>
      ) : (
        <>
          <div style={{ display: "flex", marginBottom: "2vh" }}>
            <Search
              value={searchText}
              placeholder="Search calls by Patient Name, Primary Member ID, Call Status..."
              onChange={handleSearchPage2}
              themeMode={themeMode}
              currentTheme={currentTheme}
              style={{
                flex: "1",
                marginRight: "1rem",
                outline: "none",
                border: "none",
              }}
            />
            <Button
              label={"Reset"}
              backgroundColor={"#ff4e3a"}
              width={"calc(12% - 0.5rem)"}
              height={"4vh"}
              onClick={() => handleReset()}
              style={{ marginRight: "1rem" }}
            />
            <Button
              label={"Back"}
              backgroundColor={"#ff4e3a"}
              width={"calc(12% - 0.5rem)"}
              height={"4vh"}
              onClick={() => handleBack()}
            />
          </div>
          {loading ? (
            <img
              className="loader"
              src={
                themeMode === "dark"
                  ? "/icons/loader_white.gif"
                  : "/icons/loader_black.gif"
              }
              alt="Loading"
            />
          ) : callStatusRows.length !== 0 ? (
            <CallStatus
              rows={callStatusRows}
              loading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              refresh={() =>
                handleCallProcessClick(sessionStorage.getItem("batchId"))
              }
            />
          ) : (
            "No Records Found"
          )}
        </>
      )}
      {/* Menu now provided by OverflowMenu inside each row */}
      <TypedConfirmPopup
        visible={cancelPopupVisible}
        title="Cancel Batch"
        message={
          batchToCancel ? (
            <>
              You are about to cancel batch <strong>{batchToCancel.id}</strong>.
              <br />
              Status: <strong>{batchToCancel.status}</strong>
              {/* <br />
              Cancel Before (local): <strong>{batchToCancel.cancelBeforeLocal}</strong> */}
              <br />
              <br />
              This action cannot be undone.
            </>
          ) : (
            "This action cannot be undone."
          )
        }
        confirmWord="CONFIRM"
        confirmButtonLabel="CONFIRM"
        secondaryButtonLabel="Close"
        onConfirm={confirmCancelBatch}
        onClose={closeCancelPopup}
      />

      {/* 🆕 New UI Video Modal */}
      {videoModalOpen && (
        <div
          className="video-modal-overlay"
          onClick={() => setVideoModalOpen(false)}
        >
          <div
            className="video-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="video-modal__close"
              onClick={() => setVideoModalOpen(false)}
              aria-label="Close video"
            >
              ×
            </button>
            <video
              src="/videos/new-ui-promo.mp4"
              controls
              autoPlay
              className="video-modal__player"
            />
            <button
              type="button"
              style={{
                width: "100%",
                marginTop: "1rem",
                padding: "0.75rem 1.25rem",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                color: "#ffffff",
                background: "linear-gradient(to right, #fa4d3a, #e83e2c)",
                boxShadow: "0 2px 10px rgba(250, 77, 58, 0.45)",
                transition: "background 0.18s, transform 0.15s, box-shadow 0.18s",
                position: "relative",
                zIndex: 2,
                pointerEvents: "auto",
              }}
              onClick={(e) => {
                e.stopPropagation();
                openPreviewAgentHandoff();
              }}
            >
              Experience Now →
            </button>
          </div>
        </div>
      )}

      {/* 🏢 Facility Action Required Popup */}
      <FacilityActionPopup
        visible={facilityActionPopupVisible}
        facilitiesCount={facilitiesRequiringActionCount}
        onContinue={handleFacilityActionContinue}
        loading={facilityActionLoading}
      />
    </div>
  );
};

export default Home;